import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as productsApi from '../api/productsApi';
import {
  CATEGORY_LABELS,
  FORMULA_CATEGORIES,
  FormulaItem,
  Product,
  ProductCategory,
  ProductStats,
} from '../api/productsApi';
import * as inventoryApi from '../api/inventoryApi';
import { InventoryItem } from '../types/inventory';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Modal,
  Pagination,
  Skeleton,
  Spinner,
  TableSkeleton,
} from '../components/ui';
import { IconPlus, IconSearch, IconTrash } from '../components/icons';
import OilPicker, { OilCodeTag, isUnknownOil, oilCodeMap } from '../components/OilPicker';
import { formatINR, formatINRPrecise, formatMl, round1 } from '../utils/format';
import { calculateFormulationCost } from '../utils/formulationCost';

const PAGE_SIZE = 20;

const CATEGORY_OPTIONS = Object.entries(CATEGORY_LABELS) as [ProductCategory, string][];

// These values MUST match the keys in the storefront's MOOD_CONFIGS
// (frontend/src/config/moods.ts). The mood picks the product page's colour
// theme and is what the shop's mood filter matches on, so anything outside this
// set — including a capitalised spelling — silently drops the product to the
// grey default theme and hides it from the filter. That is exactly what a free
// text input here used to allow.
const MOOD_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'romantic', label: 'Romantic' },
  { value: 'mysterious', label: 'Mysterious' },
  { value: 'energetic', label: 'Energetic' },
  { value: 'calm', label: 'Calm' },
  { value: 'fresh', label: 'Fresh' },
];

const EMPTY_STATS: ProductStats = {
  total: 0, active: 0, inactive: 0, out_of_stock: 0, no_formula: 0, in_landing: 0,
};

const EMPTY_FORM = {
  name: '',
  description: '',
  category: 'eau_de_parfum' as ProductCategory,
  mood: '',
  price: '',
  original_price: '',
  size_ml: '',
  stock_qty: '',
  // The gallery, in display order. One blank row so the form always shows an
  // input to type into; blanks are stripped on save.
  images: [''] as string[],
  notes_top: '',
  notes_middle: '',
  notes_base: '',
  tags: '',
  mood_tags: '',
  // Optional overrides for the product page's three accordion rows. Blank means
  // the storefront keeps auto-generating that row.
  accordion_smell: '',
  accordion_wear: '',
  accordion_bottle: '',
  is_active: true,
  show_in_landing: false,
};

type FormState = typeof EMPTY_FORM;

// "Rose, Jasmine ,Lavender" → ["Rose","Jasmine","Lavender"]
const splitCsv = (s: string): string[] =>
  s.split(',').map((x) => x.trim()).filter(Boolean);

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

// Parse a pasted formula JSON string into clean {name, ml} rows. Returns an
// error message (never throws) so the modal can surface it inline. Empty → [].
function parseFormulaJson(text: string): { items: FormulaItem[]; error: string } {
  const t = text.trim();
  if (!t) return { items: [], error: '' };
  let data: any;
  try {
    data = JSON.parse(t);
  } catch (e: any) {
    return { items: [], error: `Invalid JSON: ${e.message}` };
  }
  if (!Array.isArray(data)) {
    return { items: [], error: 'Expected an array, e.g. [{ "name": "Oudh", "ml": 8 }]' };
  }
  const items: FormulaItem[] = [];
  for (const d of data) {
    const name = (d?.name ?? '').toString().trim();
    const ml = Number(d?.ml);
    if (!name) return { items: [], error: 'Every item needs a non-empty "name"' };
    if (!Number.isFinite(ml) || ml <= 0) return { items: [], error: `"${name || '?'}" needs a positive "ml"` };
    // Pasted JSON goes through the same 1 dp rule as the row editor — a pasted
    // 0.8374 is not a dispensable amount just because it arrived as text.
    const rounded = round1(ml);
    if (rounded <= 0) return { items: [], error: `"${name}" rounds to 0.0 ml — use at least 0.1` };
    items.push({ name, ml: rounded });
  }
  return { items, error: '' };
}

const formulaToJson = (rows: FormulaItem[]): string =>
  JSON.stringify(
    rows.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), ml: round1(r.ml) })),
    null,
    2
  );

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full border border-slate-500/30 bg-slate-500/10 px-2.5 py-0.5 text-xs font-medium text-slate-600 dark:border-slate-400/30 dark:bg-slate-400/10 dark:text-slate-300">
      Inactive
    </span>
  );
}

function LandingBadge() {
  return (
    <span
      title="Featured in the Collection band on the landing page"
      className="inline-flex items-center rounded-full border border-gold/40 bg-gold/10 px-2.5 py-0.5 text-xs font-medium text-gold"
    >
      Landing
    </span>
  );
}

function FormulaBadge({ product }: { product: Product }) {
  if (!FORMULA_CATEGORIES.includes(product.category)) {
    return <span className="text-xs text-sub/70">N/A</span>;
  }
  if (product.formula_data && product.formula_data.length > 0) {
    return (
      <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-600 dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-300">
        ✓ Set
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
      ⚠ Missing
    </span>
  );
}

function StockCell({ qty }: { qty: number }) {
  const color = qty === 0 ? 'text-bad' : qty < 5 ? 'text-warn' : 'text-ok';
  return <span className={`font-semibold ${color}`}>{qty}</span>;
}

function Thumb({ product }: { product: Product }) {
  const primary = product.images[0] ?? product.image_url;
  const extra = Math.max(0, product.images.length - 1);

  if (primary) {
    return (
      // Relative wrapper so the "+N" gallery-count pip can sit on the corner —
      // the only tell in the table that a product carries more than one shot.
      <div className="relative h-11 w-11 shrink-0">
        <img
          src={primary}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-11 w-11 rounded-lg object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
        {extra > 0 && (
          <span className="absolute -bottom-1 -right-1 rounded-full bg-ink/80 px-1.5 text-[10px] font-semibold leading-4 text-app ring-1 ring-edge">
            +{extra}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-edge/60 text-sm font-semibold text-sub">
      {product.name.charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Gallery editor — an ordered list of image URLs.
 *
 * Order is meaningful: the first image is the product's primary shot (the one
 * the service mirrors into `image_url`, and the one the storefront leads its
 * carousel with), so rows can be moved up and down. A blank row is always kept
 * at the end to type into; blanks are stripped on save.
 */
function ImagesField({
  images,
  onChange,
}: {
  images: string[];
  onChange: (next: string[]) => void;
}) {
  const rows = images.length > 0 ? images : [''];

  const setAt = (i: number, value: string) =>
    onChange(rows.map((u, idx) => (idx === i ? value : u)));

  const removeAt = (i: number) => {
    const next = rows.filter((_, idx) => idx !== i);
    onChange(next.length > 0 ? next : ['']);
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div>
      <label className="label">Images</label>
      <p className="mb-2 text-xs text-sub">
        The first image is the primary shot. Customers can swipe through the rest on the shop and
        landing pages.
      </p>
      <div className="space-y-2">
        {rows.map((url, i) => (
          <div key={i} className="flex items-center gap-2">
            {/* Live preview — an unreachable URL simply shows the empty frame
                rather than a broken-image glyph. */}
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-edge bg-edge/40">
              {url.trim() ? (
                <img
                  src={url.trim()}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  onLoad={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = '1';
                  }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.opacity = '0';
                  }}
                />
              ) : (
                <span className="text-[10px] text-sub">{i + 1}</span>
              )}
            </div>

            <input
              className="input flex-1"
              placeholder={i === 0 ? 'https://… (primary)' : 'https://…'}
              value={url}
              onChange={(e) => setAt(i, e.target.value)}
            />

            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="btn-outline px-2 py-1 text-xs disabled:opacity-30"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label="Move image up"
                title="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="btn-outline px-2 py-1 text-xs disabled:opacity-30"
                onClick={() => move(i, 1)}
                disabled={i === rows.length - 1}
                aria-label="Move image down"
                title="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                className="btn-outline px-2 py-1 text-xs text-bad"
                onClick={() => removeAt(i)}
                aria-label="Remove image"
                title="Remove"
              >
                <IconTrash className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="btn-outline mt-2 inline-flex items-center gap-1.5 text-xs"
        onClick={() => onChange([...rows, ''])}
      >
        <IconPlus className="h-3.5 w-3.5" />
        Add image
      </button>
    </div>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [stats, setStats] = useState<ProductStats>(EMPTY_STATS);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  // Filters + pagination
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState<'all' | '1' | '0'>('all');
  const [page, setPage] = useState(1);

  // Oil inventory (autocomplete + formula check)
  const [oils, setOils] = useState<InventoryItem[]>([]);

  // Add/Edit modal
  const [editing, setEditing] = useState<Product | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Formula modal
  const [formulaProduct, setFormulaProduct] = useState<Product | null>(null);
  const [formulaRows, setFormulaRows] = useState<FormulaItem[]>([]);
  const [formulaMode, setFormulaMode] = useState<'rows' | 'json'>('rows');
  const [formulaJson, setFormulaJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [savingFormula, setSavingFormula] = useState(false);
  const [showChecks, setShowChecks] = useState(false);

  // Stock modal
  const [stockProduct, setStockProduct] = useState<Product | null>(null);
  const [stockQty, setStockQty] = useState('');
  const [savingStock, setSavingStock] = useState(false);

  // Deactivate/restore
  const [deactivateTarget, setDeactivateTarget] = useState<Product | null>(null);
  const [togglingId, setTogglingId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await productsApi.getProducts({
        search: search.trim() || undefined,
        category: category || undefined,
        is_active: status,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setProducts(res.products);
      setStats(res.stats);
      setTotal(res.total);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [search, category, status, page]);

  // Debounce so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  // Oils are only needed for the formula modal; fetch once, best-effort.
  useEffect(() => {
    inventoryApi.getInventory().then(setOils).catch(() => setOils([]));
  }, []);

  const oilQty = useMemo(() => {
    const m = new Map<string, number>();
    oils.forEach((o) => m.set(o.oil_name, o.quantity_ml));
    return m;
  }, [oils]);

  // Shelf numbers for the formula modal — this recipe is compounded off the same
  // rack the order formulas are, so the rows name the bottles the same way.
  const oilCodes = useMemo(() => oilCodeMap(oils), [oils]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };

  /* ---------- Add / Edit ---------- */

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setActionError('');
    setShowForm(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description ?? '',
      category: p.category,
      mood: p.mood ?? '',
      price: String(p.price),
      original_price: p.original_price != null ? String(p.original_price) : '',
      size_ml: p.size_ml != null ? String(p.size_ml) : '',
      stock_qty: String(p.stock_qty),
      images: p.images.length > 0 ? [...p.images] : [''],
      notes_top: p.notes_top.join(', '),
      notes_middle: p.notes_middle.join(', '),
      notes_base: p.notes_base.join(', '),
      tags: p.tags.join(', '),
      mood_tags: p.mood_tags.join(', '),
      accordion_smell: p.accordion_smell ?? '',
      accordion_wear: p.accordion_wear ?? '',
      accordion_bottle: p.accordion_bottle ?? '',
      is_active: p.is_active,
      show_in_landing: p.show_in_landing,
    });
    setActionError('');
    setShowForm(true);
  };

  const formValid = form.name.trim().length >= 2 && Number(form.price) > 0;

  const handleSave = async () => {
    setSaving(true);
    setActionError('');
    // formula_data is intentionally omitted — it's managed in the Formula modal,
    // and omitting it here preserves the existing recipe on update.
    const payload: productsApi.ProductInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      category: form.category,
      mood: form.mood.trim() || null,
      price: Number(form.price),
      original_price: numOrNull(form.original_price),
      size_ml: numOrNull(form.size_ml),
      stock_qty: numOrNull(form.stock_qty) ?? 0,
      // image_url is intentionally omitted — the service derives it from
      // images[0], so sending both would just be two sources of truth.
      images: form.images.map((u) => u.trim()).filter(Boolean),
      is_active: form.is_active,
      show_in_landing: form.show_in_landing,
      notes_top: splitCsv(form.notes_top),
      notes_middle: splitCsv(form.notes_middle),
      notes_base: splitCsv(form.notes_base),
      tags: splitCsv(form.tags),
      mood_tags: splitCsv(form.mood_tags),
      // null clears the override, putting the row back to auto-generated copy.
      accordion_smell: form.accordion_smell.trim() || null,
      accordion_wear: form.accordion_wear.trim() || null,
      accordion_bottle: form.accordion_bottle.trim() || null,
    };
    try {
      if (editing) await productsApi.updateProduct(editing.id, payload);
      else await productsApi.createProduct(payload);
      setShowForm(false);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /* ---------- Formula ---------- */

  const openFormula = (p: Product) => {
    setFormulaProduct(p);
    setFormulaRows(p.formula_data ? p.formula_data.map((f) => ({ ...f })) : []);
    setFormulaMode('rows');
    setFormulaJson('');
    setJsonError('');
    setShowChecks(false);
    setActionError('');
  };

  const formulaTotal = round1(formulaRows.reduce((sum, r) => sum + round1(r.ml), 0));

  // Oil names in this formula that match no inventory row. Each is already
  // flagged in red on its own row; counted here so the problem is visible from
  // the modal footer without scrolling a long formula to find it.
  const unknownOilCount = useMemo(
    () => formulaRows.filter((r) => isUnknownOil(r.name, oils)).length,
    [formulaRows, oils]
  );

  // Per-bottle raw-material cost of the formula being edited. In JSON mode the
  // textarea is the source of truth, so parse it (never throws → [] on error).
  const bottleCost = useMemo(() => {
    const rows = formulaMode === 'json' ? parseFormulaJson(formulaJson).items : formulaRows;
    return calculateFormulationCost(rows, oils);
  }, [formulaMode, formulaJson, formulaRows, oils]);

  const setRow = (i: number, patch: Partial<FormulaItem>) =>
    setFormulaRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  // Switching Rows → JSON serializes the current rows; JSON → Rows parses the
  // pasted text and refuses to switch if it's invalid (error shown inline).
  const switchFormulaMode = (mode: 'rows' | 'json') => {
    if (mode === formulaMode) return;
    if (mode === 'json') {
      setFormulaJson(formulaToJson(formulaRows));
      setJsonError('');
    } else {
      const { items, error } = parseFormulaJson(formulaJson);
      if (error) {
        setJsonError(error);
        return;
      }
      setFormulaRows(items);
      setJsonError('');
    }
    setFormulaMode(mode);
  };

  const handleSaveFormula = async () => {
    if (!formulaProduct) return;

    // In JSON mode the textarea is the source of truth — parse & validate it.
    let clean: FormulaItem[];
    if (formulaMode === 'json') {
      const { items, error } = parseFormulaJson(formulaJson);
      if (error) {
        setJsonError(error);
        return;
      }
      clean = items;
    } else {
      clean = formulaRows
        .map((r) => ({ name: r.name.trim(), ml: round1(r.ml) }))
        .filter((r) => r.name && r.ml > 0);
    }

    setSavingFormula(true);
    setActionError('');
    try {
      await productsApi.updateProduct(formulaProduct.id, {
        formula_data: clean.length > 0 ? clean : null,
      });
      setFormulaProduct(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSavingFormula(false);
    }
  };

  /* ---------- Stock ---------- */

  const openStock = (p: Product) => {
    setStockProduct(p);
    setStockQty(String(p.stock_qty));
    setActionError('');
  };

  const handleSaveStock = async () => {
    if (!stockProduct) return;
    const qty = Number(stockQty);
    if (!Number.isInteger(qty) || qty < 0) {
      setActionError('Stock must be a non-negative whole number');
      return;
    }
    setSavingStock(true);
    setActionError('');
    try {
      await productsApi.updateStock(stockProduct.id, qty);
      setStockProduct(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setSavingStock(false);
    }
  };

  /* ---------- Deactivate / Restore ---------- */

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setTogglingId(deactivateTarget.id);
    setActionError('');
    try {
      await productsApi.deleteProduct(deactivateTarget.id);
      setDeactivateTarget(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
      setDeactivateTarget(null);
    } finally {
      setTogglingId('');
    }
  };

  const handleRestore = async (p: Product) => {
    setTogglingId(p.id);
    setActionError('');
    try {
      await productsApi.restoreProduct(p.id);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setTogglingId('');
    }
  };

  const STAT_CARDS: { label: string; value: number; tone: string }[] = [
    { label: 'Total', value: stats.total, tone: 'text-ink' },
    { label: 'Active', value: stats.active, tone: 'text-ok' },
    { label: 'Inactive', value: stats.inactive, tone: 'text-sub' },
    { label: 'Out of stock', value: stats.out_of_stock, tone: 'text-bad' },
    { label: 'Missing formula', value: stats.no_formula, tone: 'text-warn' },
    // Zero here means the landing page's Collection band renders nothing at all,
    // which is worth being able to see without opening the site.
    { label: 'On landing', value: stats.in_landing, tone: stats.in_landing > 0 ? 'text-gold' : 'text-sub' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Products</h1>
          <p className="text-sm text-sub">Manage your catalogue</p>
        </div>
        <button className="btn-gold" onClick={openCreate}>
          <IconPlus className="h-4 w-4" />
          Add product
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}

      {/* Stats */}
      {loading && products.length === 0 ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="card p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-sub">{c.label}</p>
              <p className={`mt-2 text-2xl font-semibold ${c.tone}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1">
          <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-sub" />
          <input
            className="input !pl-9"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => resetFilter(() => setSearch(e.target.value))}
          />
        </div>
        <select
          className="input w-auto"
          value={category}
          onChange={(e) => resetFilter(() => setCategory(e.target.value))}
        >
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="input w-auto"
          value={status}
          onChange={(e) => resetFilter(() => setStatus(e.target.value as 'all' | '1' | '0'))}
        >
          <option value="all">All status</option>
          <option value="1">Active only</option>
          <option value="0">Inactive only</option>
        </select>
      </div>

      {/* Table */}
      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={8} />
        ) : products.length === 0 ? (
          <EmptyState
            message="No products found"
            hint={search || category || status !== 'all' ? 'Try adjusting your filters' : 'Add your first product'}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">Product</th>
                    <th className="th">Category</th>
                    <th className="th">Price</th>
                    <th className="th">Stock</th>
                    <th className="th">Formula</th>
                    <th className="th">Status</th>
                    <th className="th">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-edge">
                  {products.map((p) => {
                    const hasFormula = FORMULA_CATEGORIES.includes(p.category);
                    return (
                      <tr key={p.id} className="transition-colors hover:bg-edge/20">
                        <td className="td">
                          <div className="flex items-center gap-3">
                            <Thumb product={p} />
                            <div className="min-w-0">
                              <p className="truncate font-medium">{p.name}</p>
                              {p.mood && <p className="truncate text-xs text-sub">{p.mood}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="td text-sub">{CATEGORY_LABELS[p.category]}</td>
                        <td className="td">
                          <span className="font-medium">{formatINR(p.price)}</span>
                          {p.original_price != null && p.original_price > p.price && (
                            <span className="ml-1.5 text-xs text-sub line-through">
                              {formatINR(p.original_price)}
                            </span>
                          )}
                        </td>
                        <td className="td">
                          <StockCell qty={p.stock_qty} />
                        </td>
                        <td className="td">
                          <FormulaBadge product={p} />
                        </td>
                        <td className="td">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <StatusBadge active={p.is_active} />
                            {p.show_in_landing && p.is_active && <LandingBadge />}
                          </div>
                        </td>
                        <td className="td">
                          <div className="flex flex-wrap items-center gap-2">
                            <button className="btn-outline !px-3 !py-1.5 !text-xs" onClick={() => openEdit(p)}>
                              Edit
                            </button>
                            {hasFormula && (
                              <button className="btn-outline !px-3 !py-1.5 !text-xs" onClick={() => openFormula(p)}>
                                Formula
                              </button>
                            )}
                            <button className="btn-outline !px-3 !py-1.5 !text-xs" onClick={() => openStock(p)}>
                              Stock
                            </button>
                            {/* Moderation for THIS product — the Reviews page
                                filtered down, rather than a second copy of the
                                queue living inside the edit modal. */}
                            <Link
                              to={`/reviews?product_id=${encodeURIComponent(p.id)}`}
                              className="btn-outline !px-3 !py-1.5 !text-xs"
                            >
                              Reviews
                              {p.review_count > 0 && (
                                <span className="ml-1 text-sub">({p.review_count})</span>
                              )}
                            </Link>
                            {p.is_active ? (
                              <button
                                className="btn-ghost !px-3 !py-1.5 !text-xs hover:!text-bad"
                                disabled={togglingId === p.id}
                                onClick={() => setDeactivateTarget(p)}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                className="btn-ghost !px-3 !py-1.5 !text-xs hover:!text-ok"
                                disabled={togglingId === p.id}
                                onClick={() => handleRestore(p)}
                              >
                                {togglingId === p.id && <Spinner className="h-3 w-3" />}
                                Restore
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPage={setPage} />
          </>
        )}
      </Card>

      {/* Add / Edit modal */}
      <Modal
        open={showForm}
        title={editing ? 'Edit product' : 'Add product'}
        onClose={() => setShowForm(false)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setShowForm(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSave} disabled={saving || !formValid}>
              {saving && <Spinner />}
              {editing ? 'Save changes' : 'Create product'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Name *</label>
            <input
              className="input"
              placeholder="Velvet Oud"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea
              className="input min-h-[64px]"
              placeholder="A rich, smoky oud with a soft amber base…"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Category *</label>
              <select
                className="input"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as ProductCategory })}
              >
                {CATEGORY_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Mood</label>
              <select
                className="input"
                value={form.mood}
                onChange={(e) => setForm({ ...form, mood: e.target.value })}
              >
                {MOOD_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
                {/* A product saved while this was a free text field may hold a
                    value that isn't one of the five. Surface it rather than
                    rendering a blank select — otherwise an admin can't see what
                    is set and would silently save the invalid value straight
                    back. Picking any real option replaces it. */}
                {form.mood && !MOOD_OPTIONS.some((m) => m.value === form.mood) && (
                  <option value={form.mood}>{form.mood} (not a valid mood)</option>
                )}
              </select>
            </div>
            <div>
              <label className="label">Price (₹) *</label>
              <input
                className="input"
                type="number"
                min={1}
                placeholder="2499"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Original price (₹)</label>
              <input
                className="input"
                type="number"
                min={0}
                placeholder="Optional — strikethrough"
                value={form.original_price}
                onChange={(e) => setForm({ ...form, original_price: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Size (ml)</label>
              <input
                className="input"
                type="number"
                min={0}
                placeholder="50"
                value={form.size_ml}
                onChange={(e) => setForm({ ...form, size_ml: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Stock quantity</label>
              <input
                className="input"
                type="number"
                min={0}
                placeholder="0"
                value={form.stock_qty}
                onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
              />
            </div>
          </div>
          <ImagesField
            images={form.images}
            onChange={(images) => setForm({ ...form, images })}
          />
          <div>
            <label className="label">Top notes (comma separated)</label>
            <input
              className="input"
              placeholder="Bergamot, Lemon"
              value={form.notes_top}
              onChange={(e) => setForm({ ...form, notes_top: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Middle notes (comma separated)</label>
            <input
              className="input"
              placeholder="Rose, Jasmine"
              value={form.notes_middle}
              onChange={(e) => setForm({ ...form, notes_middle: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Base notes (comma separated)</label>
            <input
              className="input"
              placeholder="Oud, Musk, Amber"
              value={form.notes_base}
              onChange={(e) => setForm({ ...form, notes_base: e.target.value })}
            />
          </div>
          {/* Overrides for the three accordion rows in the product page's
              "Fragrance Details" section. Each one is independent: filling in
              only the bottle field leaves the other two auto-generated. */}
          <div className="space-y-3">
            <div>
              <label className="label">Product Page Accordion (optional)</label>
              <p className="mt-1 text-xs text-sub">Leave blank to use auto-generated content</p>
            </div>
            <div>
              <label className="label">What does it smell like? (optional)</label>
              <textarea
                className="input min-h-[64px] resize-y"
                rows={3}
                placeholder="Describe the scent in your own words — this overrides the auto-generated note description. Leave blank to auto-generate from the notes pyramid above."
                value={form.accordion_smell}
                onChange={(e) => setForm({ ...form, accordion_smell: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Wear &amp; Longevity (optional)</label>
              <textarea
                className="input min-h-[64px] resize-y"
                rows={3}
                placeholder="Describe how long it lasts, projection, best application tips. Leave blank to use the default EDP/EDT description."
                value={form.accordion_wear}
                onChange={(e) => setForm({ ...form, accordion_wear: e.target.value })}
              />
            </div>
            <div>
              <label className="label">About the bottle (optional)</label>
              <textarea
                className="input min-h-[64px] resize-y"
                rows={3}
                placeholder="Describe the packaging, bottle design, special details. Leave blank to use the default bottle description."
                value={form.accordion_bottle}
                onChange={(e) => setForm({ ...form, accordion_bottle: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Mood tags (comma separated)</label>
            <input
              className="input"
              placeholder="romantic, focus, confident"
              value={form.mood_tags}
              onChange={(e) => setForm({ ...form, mood_tags: e.target.value })}
            />
            <p className="mt-1 text-xs text-sub">
              How wearing it feels. Shown under the description on the product page — descriptive
              only, not a shop filter.
            </p>
          </div>
          <div>
            <label className="label">Tags (comma separated)</label>
            <input
              className="input"
              placeholder="bestseller, unisex"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
            />
            <p className="mt-1 text-xs text-sub">
              Merchandising labels. Clickable on the product page — they filter the shop.
            </p>
          </div>
          <label className="flex cursor-pointer items-center justify-between text-sm">
            <span className="font-medium">Active</span>
            <button
              type="button"
              role="switch"
              aria-checked={form.is_active}
              onClick={() => setForm({ ...form, is_active: !form.is_active })}
              className={`relative h-6 w-11 rounded-full transition-colors ${form.is_active ? 'bg-gold' : 'bg-edge'}`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                  form.is_active ? 'left-[22px]' : 'left-0.5'
                }`}
              />
            </button>
          </label>

          {/* Landing-page opt-in. Gated on Active because the landing band reads
              the public catalogue, which only lists active products — an
              inactive product would be flagged and still never appear. */}
          <label className={`flex items-center justify-between text-sm ${form.is_active ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
            <span>
              <span className="font-medium">Show on landing page</span>
              <span className="mt-0.5 block text-xs text-sub">
                Features this product in the Collection band on the home page.
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={form.show_in_landing}
              disabled={!form.is_active}
              onClick={() => setForm({ ...form, show_in_landing: !form.show_in_landing })}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${form.show_in_landing && form.is_active ? 'bg-gold' : 'bg-edge'}`}
            >
              <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                  form.show_in_landing ? 'left-[22px]' : 'left-0.5'
                }`}
              />
            </button>
          </label>
        </div>
      </Modal>

      {/* Formula modal */}
      <Modal
        open={formulaProduct !== null}
        title={
          formulaProduct
            ? `Oil formula — ${formulaProduct.name}${formulaProduct.size_ml ? ` (${formulaProduct.size_ml}ml)` : ''}`
            : 'Oil formula'
        }
        onClose={() => setFormulaProduct(null)}
        footer={
          <>
            <button
              className="btn-outline"
              onClick={() => setShowChecks((s) => !s)}
              disabled={formulaMode === 'json' || formulaRows.length === 0}
            >
              {showChecks ? 'Hide inventory' : 'Check inventory'}
            </button>
            <button className="btn-outline" onClick={() => setFormulaProduct(null)} disabled={savingFormula}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSaveFormula} disabled={savingFormula}>
              {savingFormula && <Spinner />}
              Save formula
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-sub">
              ml of each oil per bottle. When a customer orders this product, these oils are deducted from
              inventory (scaled by quantity). Leave empty to fall back to stock-count-only tracking.
            </p>
            {/* Rows / JSON toggle */}
            <div className="flex shrink-0 overflow-hidden rounded-lg border border-edge text-xs font-medium">
              {(['rows', 'json'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => switchFormulaMode(m)}
                  className={`px-3 py-1.5 transition-colors ${
                    formulaMode === m ? 'bg-gold text-[#1a1408]' : 'text-sub hover:text-ink'
                  }`}
                >
                  {m === 'rows' ? 'Rows' : 'JSON'}
                </button>
              ))}
            </div>
          </div>

          {formulaMode === 'json' ? (
            <div className="space-y-2">
              <textarea
                className="input min-h-[200px] font-mono text-xs"
                spellCheck={false}
                placeholder={'[\n  { "name": "Oudh", "ml": 8 },\n  { "name": "Rose", "ml": 4 }\n]'}
                value={formulaJson}
                onChange={(e) => {
                  setFormulaJson(e.target.value);
                  if (jsonError) setJsonError('');
                }}
              />
              {jsonError && <p className="text-xs text-bad">{jsonError}</p>}
              <p className="text-xs text-sub">
                Paste an array of <span className="font-mono">{'{ "name", "ml" }'}</span> objects. Switching back
                to Rows (or saving) validates it.
              </p>
            </div>
          ) : formulaRows.length === 0 ? (
            <p className="rounded-lg border border-dashed border-edge px-3 py-4 text-center text-sm text-sub">
              No oils mapped yet.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wide text-sub">
                <span className="w-8 shrink-0 text-right">#</span>
                <span className="flex-1">Oil name</span>
                <span className="w-20">ml</span>
                {showChecks && <span className="w-24">In stock</span>}
                <span className="w-6" />
              </div>
              {formulaRows.map((row, i) => {
                const available = oilQty.get(row.name.trim());
                const sufficient = available != null && available >= round1(row.ml);
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-8 shrink-0 pt-2 text-right leading-none">
                      <OilCodeTag code={oilCodes.get(row.name.trim())} />
                    </span>
                    {/* Picking from the list writes inventory's exact oil_name.
                        A name that matches nothing flags itself in red — this
                        product's oils would not deduct as it stands. */}
                    <OilPicker
                      className="flex-1"
                      placeholder="Oudh"
                      value={row.name}
                      oils={oils}
                      onChange={(name) => setRow(i, { name })}
                    />
                    <input
                      className="input w-20 shrink-0"
                      type="number"
                      min={0}
                      step="0.1"
                      placeholder="0"
                      value={row.ml === 0 && !row.name ? '' : row.ml}
                      onChange={(e) => setRow(i, { ml: round1(parseFloat(e.target.value)) })}
                    />
                    {showChecks && (
                      <span className={`w-24 shrink-0 py-2 text-xs ${available == null ? 'text-sub' : sufficient ? 'text-ok' : 'text-bad'}`}>
                        {available == null ? 'not found' : `${sufficient ? '✓' : '✗'} ${formatMl(available)}`}
                      </span>
                    )}
                    <button
                      className="btn-ghost shrink-0 !p-1.5 hover:!text-bad"
                      aria-label="Remove oil"
                      onClick={() => setFormulaRows((rows) => rows.filter((_, idx) => idx !== i))}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* The <datalist> that used to back these inputs is gone: it suggested
              names but happily accepted anything typed over them, and a name
              that differs from inventory by case or spacing never deducts.
              OilPicker writes the canonical oil_name and flags one that isn't. */}

          {/* Existing bad data, stated plainly. These oils will not deduct when
              the product sells, and nothing downstream reports that failure. */}
          {formulaMode === 'rows' && unknownOilCount > 0 && (
            <p className="rounded-lg border border-bad/40 bg-bad/10 px-3 py-2 text-xs text-bad">
              {unknownOilCount} oil{unknownOilCount === 1 ? '' : 's'} in this formula
              {unknownOilCount === 1 ? ' does' : ' do'} not match any inventory row and will not
              deduct when this product sells. Pick the name from the dropdown to fix it.
            </p>
          )}

          {formulaMode === 'rows' && (
            <div className="flex items-center justify-between border-t border-edge pt-3">
              <button
                className="btn-outline !px-3 !py-1.5 !text-xs"
                onClick={() => setFormulaRows((rows) => [...rows, { name: '', ml: 0 }])}
              >
                <IconPlus className="h-3.5 w-3.5" />
                Add oil
              </button>
              <span className="text-sm text-sub">
                Total:{' '}
                <span className="font-semibold text-ink">{formatMl(formulaTotal)}</span>
                {formulaProduct?.size_ml ? ` / ${formulaProduct.size_ml} ml bottle` : ''}
              </span>
            </div>
          )}

          {/* Estimated per-bottle raw-material cost — internal, never shown to customers */}
          {bottleCost.breakdown.length > 0 && (
            <div className="rounded-xl border border-edge bg-edge/10 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Estimated Cost per Bottle</span>
                <span className="text-sm font-semibold">{formatINRPrecise(bottleCost.total)}</span>
              </div>
              {/* Oils only, untaxed — GST is a customer price, not a cost. */}
              <p className="mt-1 text-xs text-sub">Raw material cost of the oils in this formula</p>
              {!bottleCost.isComplete && (
                <p className="mt-2 text-xs text-warn">
                  ⚠ No cost set for {bottleCost.missingPricing.join(', ')}. Add pricing on the{' '}
                  <Link to="/inventory" className="font-medium underline">
                    Inventory page
                  </Link>
                  .
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Stock modal */}
      <Modal
        open={stockProduct !== null}
        title="Update stock"
        onClose={() => setStockProduct(null)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setStockProduct(null)} disabled={savingStock}>
              Cancel
            </button>
            <button className="btn-gold" onClick={handleSaveStock} disabled={savingStock}>
              {savingStock && <Spinner />}
              Update
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Product</label>
            <input className="input opacity-70" value={stockProduct?.name ?? ''} readOnly />
          </div>
          <div>
            <label className="label">
              Stock quantity {stockProduct && <span className="text-sub">(current: {stockProduct.stock_qty})</span>}
            </label>
            <input
              className="input"
              type="number"
              min={0}
              value={stockQty}
              onChange={(e) => setStockQty(e.target.value)}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deactivateTarget !== null}
        title="Deactivate product?"
        message={
          <>
            Deactivating <span className="font-semibold text-ink">{deactivateTarget?.name}</span> will hide
            it from the shop. Existing orders are unaffected, and you can restore it anytime.
          </>
        }
        confirmLabel="Deactivate"
        danger
        busy={togglingId === deactivateTarget?.id}
        onConfirm={handleDeactivate}
        onClose={() => setDeactivateTarget(null)}
      />
    </div>
  );
}
